import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import DropContainer from "./DropContainer";
import * as actions from '../../../../store/actions';
import { RootState } from "../../../../store/types";
import ScheduledServicesByUserAndDayModal from "../Modals/ScheduledServicesByUserAndDayModal";
import { i18n } from "../../../../translate/i18n";

type Props = {
  redirect: CallableFunction
}

const PlannerCalendar: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = now.getDate();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysArray: { month: number, year: number, day: number }[] = [];
  for (let dayIndex = 1; dayIndex <= daysInMonth; dayIndex  += 1) {
    daysArray.push({month, year, day: dayIndex })
  }

  useEffect(() => {
    dispatch(actions.countScheduledServicesByUsersAndDays());
  }, [])

  const users = useSelector((state: RootState) => state.scheduledServices.users);
  const servicesCountByUsersAndDays = useSelector((state: RootState) => state.scheduledServices.servicesCountByUsersAndDays);
  const filters = useSelector((state: RootState) => state.scheduledServices.filtersServicesForScheduling);
  let language = i18n.language;

  useEffect(() => {
    scrollToPresentDay();
  }, [users.length, filters, language])
  
  const presentDayRef = useRef<HTMLDivElement>(null);
  const scrollToPresentDay = () => presentDayRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });

  const getWeekDayName = (date: Date) => {
    if (language === 'esp-ES') {
      language = 'es-ES';
    };
    return date.toLocaleDateString(language, { weekday: 'short' }).slice(0, 3).toLocaleLowerCase();
  }

  return (
    <>
      <Box sx={{ marginLeft: '3.5rem' }}>
        <Box sx={styles.dayTextContainer}>
          {users.length > 0 && daysArray.map((day) => (
            <Box
              key={day.day}
              sx={{...styles.dayText, color: day.day === today ? 'green' : 'black'}}
              ref={day.day === today - 1 ? presentDayRef : null}
            >
              <Typography variant="subtitle1" fontWeight={600}>{day.day}</Typography>
              <Typography variant="subtitle2">{getWeekDayName(new Date(year, month, day.day))}</Typography>
            </Box>))
          }
        </Box>
        { users.map((user) => {
          const userWithServices = servicesCountByUsersAndDays.find((userDates) => userDates._id === user._id);
          return (
            <Box key={user._id}  sx={styles.calendarContainer}>
              {daysArray.map((day) => { 
                let completedServicesCount = 0;
                let notCompletedServicesCount = 0;
                if (userWithServices) {
                  const dateWithTotalServices = userWithServices.dates.find((date) => Number(date.day) === day.day );
                  if (dateWithTotalServices) {
                    completedServicesCount = dateWithTotalServices.totalCompleted;
                    notCompletedServicesCount = dateWithTotalServices.totalNotCompleted;
                  }
                }
                return (
                  <Box key={day.day} sx={styles.dayContainer}>
                    <DropContainer date={day} user={user} completedServicesCount={completedServicesCount} notCompletedServicesCount={notCompletedServicesCount} />
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Box>
      <ScheduledServicesByUserAndDayModal redirect={props.redirect}/>
    </>
  );
};

export default PlannerCalendar;

const styles = {
  calendarContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    overflowX: 'auto',
    width: '100%',
    marginBottom: '10px',
  },
  dayContainer: {
    width: '33%',
  },
  dayText: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    height: '4rem',
    width: '15%',
    marginTop: '-1rem',
  },
  dayTextContainer: {
    display: 'flex',
    position: 'sticky',
    backgroundColor: '#fff',
    top: 0,
    borderBottom: '1px solid lightGrey'
  },
};
